// Libs
import classNames from 'classnames/bind';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseToolbar } from '~/components/common';
// Others
import { RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { IDetailVenue } from '~/utils/interface/venue';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailVenue } from '~/thunks/venue/venueThunk';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { VENUE_PERMISSIONS } from '~/utils/constants/permissions';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { formatPhoneNumber, getFileNameFromUri, getTypeFileFromUri } from '~/utils/helper/common';
// Styles, images, icons
import styles from './VenueDetail.module.scss';
import { useEffect, useState } from 'react';

import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const VenueDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canEditVenue } = usePermissions(VENUE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/venue', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: venueId } = useParams();
  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [detailVenue, setDetailVenue] = useState<IDetailVenue>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!venueId) return;

    handleGetDetailVenue(venueId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailVenue = (idCategory: string) => {
    if (!idCategory) return;

    dispatch(getDetailVenue(idCategory))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setDetailVenue(res?.data);
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleEdit = () => {
    if (!venueId) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateVenue(venueId));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateVenue(venueId));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateVenue(venueId));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateVenue(venueId));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateVenue(venueId));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateVenue(venueId));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateVenue(venueId));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateVenue(venueId));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateVenue(venueId));
        break;
    }
  };

  const handleOnBack = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.venue);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.venue);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.venue);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.venue);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.venue);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.venue);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.venue);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.venue);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.venue);
        break;
    }
  };

  const renderValueItem = (label: string, value: string) => {
    return (
      <Stack gap={'7px'}>
        <Typography className={cx('label')}>{label}</Typography>
        <Typography className={cx('value')}>{value}</Typography>
      </Stack>
    );
  };

  const renderItemDocument = (url: string) => {
    return (
      <Stack className={cx('itemDocument')}>
        <Stack gap={4} direction={'row'} alignItems={'center'}>
          <Box className={cx('bgIcon')}>
            <img src={icons.commonIconFileDocument} alt={t('altImg', { ns: 'common/base' })} />
          </Box>

          <Box>
            <Typography className={cx('labelFile')}>{getFileNameFromUri(url)}</Typography>
            <Typography className={cx('valueFile')}>{`${getTypeFileFromUri(url)}`}</Typography>
          </Box>
        </Stack>

        <Button onClick={() => window.open(url)}>
          <Typography className={cx('textDownload')}>{t('common_text_download', { ns: 'common/base' })}</Typography>
        </Button>
      </Stack>
    );
  };
  //#endregion Handle Function

  return (
    <Box id='venueDetailPage' className={cx('container')}>
      <BaseToolbar title={t('venue_detail_title')} isBack onBackToPreviousPage={handleOnBack}>
        {canEditVenue && (
          <Button variant='contained' className={cx('buttonEdit')} onClick={handleEdit}>
            {t('venue_detail_title_edit')}
          </Button>
        )}
      </BaseToolbar>

      <Stack className={cx('body')}>
        <Grid2 size={8} container spacing={6}>
          <Grid2 size={6} className={cx('itemBox')}>
            <Stack gap={5}>
              <Typography className={cx('textHeader')}>{t('venue_detail_title_base_information')}</Typography>

              {renderValueItem(t('venue_detail_title_name'), detailVenue?.name || EMPTY_VALUE)}

              {renderValueItem(t('venue_detail_title_contact'), detailVenue?.contactName || EMPTY_VALUE)}

              {renderValueItem(t('venue_detail_title_email'), detailVenue?.email || EMPTY_VALUE)}

              {renderValueItem(
                t('venue_detail_title_phone'),
                formatPhoneNumber(detailVenue?.phoneNumber) || EMPTY_VALUE
              )}

              {renderValueItem(t('venue_detail_title_address'), detailVenue?.address || EMPTY_VALUE)}

              <Stack gap={'7px'}>
                <Typography className={cx('label')}>{t('venue_detail_title_website')}</Typography>
                <Typography
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (detailVenue?.website) window.open(detailVenue?.website);
                  }}
                  className={cx('textWebsite')}
                >
                  {detailVenue?.website || EMPTY_VALUE}
                </Typography>
              </Stack>
            </Stack>
          </Grid2>

          <Grid2 size={6} className={cx('itemBox')}>
            <Stack gap={5}>
              <Typography className={cx('textHeader')}>{t('venue_detail_title_note')}</Typography>

              <Typography className={cx('valueDescription')}>{detailVenue?.note || EMPTY_VALUE}</Typography>
            </Stack>
          </Grid2>
        </Grid2>

        <Stack gap={6} className={cx('itemBox')}>
          <Typography className={cx('textHeader')}>{t('venue_detail_title_attachment')}</Typography>

          <Stack gap={4}>
            {detailVenue?.venueFile ? (
              renderItemDocument(detailVenue?.venueFile)
            ) : (
              <Typography align='center' className={cx('noData')}>
                {t('common_no_data_available', { ns: 'common/base' })}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default VenueDetail;
